import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import moment from "moment";
import { fetchWithRetry } from "../util/fetchWithRetry";
import LineChart from "./lineChart";

const MADE_BY_OPTIONS = [
  { key: "all", label: "All" },
  { key: "created", label: "Created" },
  { key: "callCenter", label: "Call Center" },
  { key: "online", label: "Online" },
  { key: "mindBody", label: "MindBody" },
];

// For a given day's bucket, return the appointment count selected by `madeBy`.
//  - "all"      → totalAppointments scheduled that day (Mindbody staffappointments)
//  - "created"  → all appointments booked that day, any source, scheduled date irrelevant
//  - others     → bookings created that day via that single source
const pickAppointmentCount = (day, madeBy) => {
  if (madeBy === "all") return day.totalAppointments;
  if (!day.origin) return 0;
  if (madeBy === "created") {
    return (
      (day.origin.online || 0) +
      (day.origin.callCenter || 0) +
      (day.origin.mindBody || 0)
    );
  }
  return day.origin[madeBy] || 0;
};

const OperationalDashboardHistory = ({ locationsAuth }) => {
  const [status, setStatus] = useState("idle");
  const [historyData, setHistoryData] = useState([]);
  const [failed, setFailed] = useState([]);
  const [madeBy, setMadeBy] = useState("all");

  // last 30 days, ending yesterday (today is still in progress)
  const startDate = moment().subtract(30, "days").format("MM/DD/YYYY");
  const endDate = moment().subtract(1, "days").format("MM/DD/YYYY");

  const getLocationHistory = async (location) => {
    const { siteId, locationId, siteName, token } = location;
    const getMethod = {
      method: "GET",
      headers: {
        "Content-type": "application/json; charset=UTF-8",
        siteid: siteId,
        authorization: token,
        locationid: locationId,
      },
    };

    const url = `/api/site/${siteId}/locations/${locationId}/summary/history?startDate=${startDate}&endDate=${endDate}`;
    // The backend builds one summary per day of the range, so this endpoint is
    // legitimately slow (~2-10s per site). The default 10s timeout used to
    // abort responses that were about to land — give it room and retry once.
    const response = await fetchWithRetry(
      `${process.env.REACT_APP_API_URL}${url}`,
      getMethod,
      {
        timeoutMs: 60000,
        retries: 1,
        context: `History ${siteName} (${siteId}-${locationId})`,
      }
    );

    if (response.ok) {
      const body = await response.json();
      return Promise.resolve({
        data: body.history || [],
        siteId: siteId,
        locationId: locationId,
        siteName: siteName,
      });
    } else {
      return Promise.resolve({
        error:
          "Failed to retrieve data from site : " +
          siteName +
          " " +
          siteId +
          " at location: " +
          locationId,
        siteId: siteId,
        locationId: locationId,
        siteName: siteName,
      });
    }
  };

  const getData = async () => {
    setStatus("loading");
    try {
      // Fetch a few locations at a time instead of all ~30 at once: each
      // history request fans out to MindBody per day on the backend, so full
      // parallelism made every request slow enough to time out.
      const CONCURRENT_LOCATIONS = 4;
      const data = [];
      for (let i = 0; i < locationsAuth.length; i += CONCURRENT_LOCATIONS) {
        const batch = locationsAuth
          .slice(i, i + CONCURRENT_LOCATIONS)
          .map((loc) => getLocationHistory(loc));
        data.push(...(await Promise.all(batch)));
      }
      setHistoryData(data.filter((item) => "data" in item));
      setFailed(data.filter((item) => "error" in item));
      setStatus("ready");
    } catch (e) {
      console.log(e);
      setHistoryData([]);
      setFailed([]);
      setStatus("error");
    }
  };

  useEffect(() => {
    getData();
  }, []);

  if (status === "idle" || status === "loading") {
    return (
      <div className="loading" data-cy="loading-history-message">
        <div className="lb-spinner" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="mt-1 mx-auto mb-3 lb-form-submited-error">
        <span data-cy="error-message">Failed to load history data</span>
      </div>
    );
  }

  return (
    <div data-cy="operational-history">
      <div className="d-flex justify-content-center mb-3">
        <div className="lb-nav lb-nav-tabs">
          {MADE_BY_OPTIONS.map((option) => (
            <div className="lb-nav-item" key={option.key}>
              <button
                className={
                  madeBy === option.key
                    ? "lb-nav-link active shadow-sm"
                    : "lb-nav-link shadow"
                }
                data-cy={`made-by-${option.key}`}
                onClick={() => setMadeBy(option.key)}
              >
                {option.label}
              </button>
            </div>
          ))}
        </div>
      </div>

      {historyData.length > 0 && (() => {
        // Sum every metric across every branch for each day in the range.
        // Labels come from the first location since all share the same range.
        const baseDays = historyData[0].data;
        const combined = baseDays.map((day, i) => {
          let totalAppointments = 0;
          let totalSales = 0;
          let online = 0;
          let callCenter = 0;
          let mindBody = 0;
          historyData.forEach((location) => {
            const d = location.data[i];
            if (!d) return;
            totalAppointments += d.totalAppointments || 0;
            totalSales += d.totalSales || 0;
            if (d.origin) {
              online += d.origin.online || 0;
              callCenter += d.origin.callCenter || 0;
              mindBody += d.origin.mindBody || 0;
            }
          });
          return {
            date: day.date,
            totalAppointments,
            totalSales,
            origin: { online, callCenter, mindBody },
          };
        });
        const combinedLabels = combined.map((d) => d.date);
        const combinedAppointments = combined.map((d) =>
          pickAppointmentCount(d, madeBy)
        );
        const combinedSales = combined.map((d) => d.totalSales);
        return (
          <div className="row" data-cy="operational-history-all-locations">
            <div className="col-12 mb-4">
              <div className="card text-center">
                <div className="card-header lb-title-operational">
                  <h4>All locations</h4>
                </div>
                <div className="card-body">
                  <div className="lb-height-line">
                    <LineChart
                      labels={combinedLabels}
                      appointments={combinedAppointments}
                      sales={combinedSales}
                    />
                  </div>
                </div>
                <div className="card-footer">
                  <small>
                    {startDate} - {endDate} &middot; {historyData.length}{" "}
                    location{historyData.length === 1 ? "" : "s"}
                  </small>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="row">
        {historyData.map((location) => {
          const labels = location.data.map((day) => day.date);
          const appointments = location.data.map((day) =>
            pickAppointmentCount(day, madeBy)
          );
          const sales = location.data.map((day) => day.totalSales);

          return (
            <div
              className="col-12 col-md-6 mb-4"
              key={location.siteId + "-" + location.locationId}
            >
              <div className="card text-center">
                <div className="card-header lb-title-operational">
                  <h4>{location.siteName}</h4>
                </div>
                <div className="card-body">
                  <div className="lb-height-line">
                    <LineChart
                      labels={labels}
                      appointments={appointments}
                      sales={sales}
                    />
                  </div>
                </div>
                <div className="card-footer">
                  <small>
                    {startDate} - {endDate}
                  </small>
                </div>
              </div>
            </div>
          );
        })}

        {failed.length > 0 &&
          failed.map((item) => {
            return (
              <div
                className="mt-1 mx-auto mb-3 lb-form-submited-error"
                key={item.siteId + "-" + item.locationId}
              >
                <span
                  data-cy={
                    "error-message-" + item.siteId + "-" + item.locationId
                  }
                >
                  {item.error}
                </span>
              </div>
            );
          })}
      </div>
    </div>
  );
};

OperationalDashboardHistory.propTypes = {
  locationsAuth: PropTypes.array,
};
export default OperationalDashboardHistory;
